// Header tools for every interface version (2026-10-04): the Error Hunter button, the Error Scan button (T-358) and the notepad.
//  - Error Hunter: opens Claude Code on this machine with a prompt typed in (not sent until you press Enter) that reads all
//    system error logs and looks for the causes and fixes. The prompt is also copied to the clipboard, so if Claude Code's
//    link handler is not registered on this machine you can paste it.
//  - Notes: a panel that drops down from the header; short notes are saved on the server (shared/gui_notes.py) tagged with
//    the interface version and page they were written on. Enter saves, Shift+Enter adds a line, Alt+N toggles, Esc closes.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Bug, StickyNote, X, Trash2, ScanSearch } from 'lucide-react'
import { notes as notesApi } from '../lib/api'
import { ERROR_HUNTER_PROMPT, errorHunterLink } from '../lib/errorHunter'
import { ERROR_SCAN_PROMPT, errorScanLink } from '../lib/errorScan'   // T-358
import { notesPrompt, notesLink, relTag } from '../lib/notes'
import { timeAgo } from '../lib/utils'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const DRAFT_KEY = 'sf_note_draft'
const readDraft = () => { try { return window.localStorage.getItem(DRAFT_KEY) || '' } catch { return '' } }
const writeDraft = (v) => { try { window.localStorage.setItem(DRAFT_KEY, v) } catch { /* the draft is a convenience only */ } }

function HeaderButton({ children, onClick, href, title, label, active, tone = 'amber', badge }) {
  const colors = tone === 'amber'
    ? { c: '#fcd34d', b: 'rgba(245,158,11,0.5)', bg: active ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.07)' }
    : { c: '#7dd3fc', b: 'rgba(56,189,248,0.45)', bg: active ? 'rgba(56,189,248,0.18)' : 'rgba(56,189,248,0.07)' }
  const style = { ...MONO, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', color: colors.c, border: `1px solid ${colors.b}`, background: colors.bg,
                  borderRadius: 6, padding: '5px 9px', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', textDecoration: 'none', cursor: 'pointer', position: 'relative' }
  const inner = <>{children}{badge > 0 && <span style={{ background: '#f59e0b', color: '#111', borderRadius: 8, padding: '0 5px', fontSize: '0.6rem' }}>{badge}</span>}</>
  return href
    ? <a href={href} onClick={onClick} title={title} aria-label={label} style={style}>{inner}</a>
    : <button onClick={onClick} title={title} aria-label={label} aria-expanded={active} style={style}>{inner}</button>
}

export function ErrorHunterButton() {
  const [copied, setCopied] = useState(false)
  const onClick = () => {
    try { navigator.clipboard.writeText(ERROR_HUNTER_PROMPT).then(() => { setCopied(true); setTimeout(() => setCopied(false), 3500) }).catch(() => {}) } catch { /* clipboard blocked: the link still works */ }
  }
  return (
    <span style={{ position: 'relative' }}>
      <HeaderButton href={errorHunterLink()} onClick={onClick} tone="amber" label="Error Hunter"
        title="Opens Claude Code with a prompt that checks every system error log and looks for the causes and fixes. Nothing is sent until you press Enter. The prompt is also copied.">
        <Bug className="h-3.5 w-3.5" /><span className="hidden lg:inline">ERROR HUNTER</span>
      </HeaderButton>
      {copied && (
        <span style={{ ...MONO, position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 60, fontSize: '0.64rem', color: '#e2e8f0', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap' }}>
          prompt copied too: paste it if Claude did not open
        </span>
      )}
    </span>
  )
}

// Error Scan (T-358): opens Claude Code with a prompt that runs the read-only dashboard crawl and checks its findings
// against the live system. Same pattern as Error Hunter: nothing runs until you press Enter; the prompt is also copied.
export function ErrorScanButton() {
  const [copied, setCopied] = useState(false)
  const onClick = () => {
    try { navigator.clipboard.writeText(ERROR_SCAN_PROMPT).then(() => { setCopied(true); setTimeout(() => setCopied(false), 3500) }).catch(() => {}) } catch { /* clipboard blocked: the link still works */ }
  }
  return (
    <span style={{ position: 'relative' }}>
      <HeaderButton href={errorScanLink()} onClick={onClick} tone="sky" label="Error Scan"
        title="Opens Claude Code with a prompt that crawls every dashboard page, link, button and script (read-only) and checks the findings against the live system. Nothing runs until you press Enter. The prompt is also copied.">
        <ScanSearch className="h-3.5 w-3.5" /><span className="hidden lg:inline">ERROR SCAN</span>
      </HeaderButton>
      {copied && (
        <span style={{ ...MONO, position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 60, fontSize: '0.64rem', color: '#e2e8f0', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap' }}>
          prompt copied too: paste it if Claude did not open
        </span>
      )}
    </span>
  )
}

export function NotesTool({ mode, page }) {
  const [open, setOpen] = useState(false)
  const [shown, setShown] = useState(false)   // drives the drop-down animation
  const [items, setItems] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [draft, setDraft] = useState(readDraft)
  const [saving, setSaving] = useState(false)
  const [filter, setFilter] = useState('open')   // open | all | here
  const [copied, setCopied] = useState(false)
  const box = useRef(null)
  const area = useRef(null)

  const load = useCallback(async () => {
    try { const r = await notesApi.list(); if (r.success) { setItems(r.notes || []); setError(null) } else setError(r.error || 'could not load notes') }
    catch (e) { setError(String(e.message || e)) }
    finally { setLoaded(true) }
  }, [])
  useEffect(() => { load() }, [load])

  const openPanel = useCallback(() => { setOpen(true); requestAnimationFrame(() => requestAnimationFrame(() => setShown(true))); load() }, [load])
  const closePanel = useCallback(() => { setShown(false); setTimeout(() => setOpen(false), 160) }, [])
  const toggle = useCallback(() => (open ? closePanel() : openPanel()), [open, openPanel, closePanel])

  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && e.key.toLowerCase() === 'n') { e.preventDefault(); toggle() }
      else if (e.key === 'Escape' && open) closePanel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle, open, closePanel])
  useEffect(() => { if (shown && area.current) area.current.focus() }, [shown])
  // other parts of the app (the reminder pop-up) can open the notepad with window.dispatchEvent(new CustomEvent('sf:open-notes'))
  useEffect(() => {
    const onOpen = () => { if (!open) openPanel() }
    window.addEventListener('sf:open-notes', onOpen)
    return () => window.removeEventListener('sf:open-notes', onOpen)
  }, [open, openPanel])
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (box.current && !box.current.contains(e.target) && !e.target.closest('[data-notes-toggle]')) closePanel() }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, closePanel])

  const save = async () => {
    const text = draft.trim()
    if (!text || saving) return
    setSaving(true)
    try {
      const r = await notesApi.add(text, mode, page)
      if (r.success && r.note) { setItems(prev => [r.note, ...prev]); setDraft(''); writeDraft(''); setError(null) } else setError(r.error || 'could not save the note')
    } catch (e) { setError(`could not save: ${String(e.message || e)}. Your text is still in the box.`) }
    finally { setSaving(false) }
  }
  const toggleDone = async (n) => {
    setItems(prev => prev.map(x => (x.id === n.id ? { ...x, done: !x.done } : x)))
    try { await notesApi.done(n.id, !n.done) } catch { load() }
  }
  const remove = async (n) => {
    setItems(prev => prev.filter(x => x.id !== n.id))
    try { await notesApi.remove(n.id) } catch { load() }
  }
  const visible = useMemo(() => items.filter(n => (filter === 'open' ? !n.done : filter === 'here' ? n.version === mode : true)), [items, filter, mode])
  const openCount = items.filter(n => !n.done).length
  const link = notesLink(items)

  const copyOpen = () => {
    const p = notesPrompt(items)
    if (!p) return
    try { navigator.clipboard.writeText(p).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500) }).catch(() => {}) } catch { /* blocked */ }
  }

  return (
    <>
      <span data-notes-toggle="1">
        <HeaderButton onClick={toggle} active={open} tone="sky" label="Notes" badge={openCount} title="Notepad: drop short notes while you work (Alt+N)">
          <StickyNote className="h-3.5 w-3.5" /><span className="hidden lg:inline">NOTES</span>
        </HeaderButton>
      </span>
      {open && (
        <div ref={box} role="dialog" aria-label="Notepad"
          style={{ position: 'fixed', top: 56, right: 12, zIndex: 70, width: 'min(460px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column',
                   background: '#0f172a', border: '1px solid #334155', borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.55)', transformOrigin: 'top right',
                   transform: shown ? 'scale(1)' : 'scale(0.92) translateY(-8px)', opacity: shown ? 1 : 0, transition: 'transform 160ms ease, opacity 160ms ease' }}>
          <div className="flex items-center gap-2" style={{ padding: '10px 12px', borderBottom: '1px solid #1e293b' }}>
            <StickyNote className="h-4 w-4" style={{ color: '#7dd3fc' }} />
            <strong style={{ fontSize: '0.9rem', color: '#e2e8f0', fontFamily: '-apple-system, Segoe UI, system-ui, sans-serif' }}>Notes</strong>
            <span style={{ ...MONO, fontSize: '0.64rem', color: '#8b9bb0' }}>{openCount} open · tagged {String(mode).toUpperCase()} {page}</span>
            <span className="flex-1" />
            <button onClick={closePanel} aria-label="Close notes" style={{ color: '#94a3b8' }}><X className="h-4 w-4" /></button>
          </div>
          <div style={{ padding: 12, display: 'grid', gap: 8 }}>
            <textarea ref={area} value={draft} rows={3} placeholder="Drop a note…  Enter saves · Shift+Enter new line"
              onChange={e => { setDraft(e.target.value); writeDraft(e.target.value) }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save() } }}
              style={{ width: '100%', resize: 'vertical', background: '#0b1220', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: '8px 10px', fontSize: '0.88rem', fontFamily: '-apple-system, Segoe UI, system-ui, sans-serif', outline: 'none' }} />
            <div className="flex items-center gap-2">
              <button onClick={save} disabled={saving || !draft.trim()}
                style={{ ...MONO, fontSize: '0.7rem', fontWeight: 700, padding: '7px 14px', borderRadius: 8, background: draft.trim() ? '#38bdf8' : '#1e293b', color: draft.trim() ? '#04121c' : '#64748b', cursor: draft.trim() ? 'pointer' : 'default' }}>
                {saving ? 'SAVING…' : 'SAVE NOTE'}
              </button>
              <span className="flex-1" />
              {[['open', 'Open'], ['here', `This version`], ['all', 'All']].map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} aria-pressed={filter === k}
                  style={{ ...MONO, fontSize: '0.64rem', padding: '3px 8px', borderRadius: 6, border: `1px solid ${filter === k ? '#0e7490' : '#334155'}`, color: filter === k ? '#67e8f9' : '#94a3b8', background: filter === k ? 'rgba(8,145,178,0.12)' : 'transparent' }}>
                  {label}
                </button>
              ))}
            </div>
            {error && <div style={{ ...MONO, fontSize: '0.68rem', color: '#fca5a5', border: '1px solid rgba(248,113,113,0.5)', background: 'rgba(127,29,29,0.3)', borderRadius: 6, padding: '6px 8px' }}>{error}</div>}
          </div>
          <div style={{ overflowY: 'auto', padding: '0 12px', flex: 1, minHeight: 40 }}>
            {!loaded && <p style={{ ...MONO, fontSize: '0.7rem', color: '#8b9bb0', padding: '8px 0' }}>loading…</p>}
            {loaded && visible.length === 0 && <p style={{ ...MONO, fontSize: '0.7rem', color: '#8b9bb0', padding: '8px 0' }}>{filter === 'open' ? 'No open notes.' : 'No notes here.'}</p>}
            {visible.map(n => (
              <div key={n.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, padding: '8px 0', borderTop: '1px solid #1e293b', alignItems: 'start' }}>
                <input type="checkbox" checked={n.done} onChange={() => toggleDone(n)} aria-label="Done" style={{ marginTop: 3, width: 16, height: 16 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.86rem', color: n.done ? '#64748b' : '#e2e8f0', textDecoration: n.done ? 'line-through' : 'none', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: '-apple-system, Segoe UI, system-ui, sans-serif' }}>{n.body}</div>
                  <div style={{ ...MONO, fontSize: '0.62rem', color: '#8b9bb0', marginTop: 2 }}>{relTag(n)} · {timeAgo(n.created_at)}</div>
                </div>
                <button onClick={() => remove(n)} aria-label="Delete note" style={{ color: '#64748b', padding: 4 }}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2" style={{ padding: '10px 12px', borderTop: '1px solid #1e293b' }}>
            <button onClick={copyOpen} disabled={openCount === 0} style={{ ...MONO, fontSize: '0.64rem', padding: '5px 10px', borderRadius: 6, border: '1px solid #334155', color: openCount ? '#cbd5e1' : '#475569' }}>
              {copied ? 'COPIED' : 'COPY OPEN NOTES'}
            </button>
            {link
              ? <a href={link} title="Opens Claude Code with your open notes typed in as a prompt. Nothing is sent until you press Enter." style={{ ...MONO, fontSize: '0.64rem', fontWeight: 700, padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(245,158,11,0.5)', color: '#fcd34d', textDecoration: 'none' }}>SEND TO CLAUDE</a>
              : <span style={{ ...MONO, fontSize: '0.64rem', color: '#475569' }}>SEND TO CLAUDE</span>}
          </div>
        </div>
      )}
    </>
  )
}
